export interface ToastItem {
  id: string
  title?: string
  message?: string
  color?: 'primary' | 'success' | 'danger' | 'warning' | 'info' | 'muted'
  icon?: string
  closable?: boolean
  duration?: number
  createdAt: number
  progress: number
}

export interface ToastOptions {
  duration?: number
  position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left' | 'top-center' | 'bottom-center'
}

export const useToasterStore = defineStore('toaster', () => {
  const toasts = ref<ToastItem[]>([])
  const defaultDuration = 5000 // 5 seconds
  const intervals = new Map<string, NodeJS.Timeout>()

  function generateId(): string {
    return Math.random().toString(36).substring(2) + Date.now().toString(36)
  }

  function addToast(
    props: Omit<ToastItem, 'id' | 'createdAt' | 'progress'>,
    options: ToastOptions = {},
  ): string {
    const id = generateId()
    const duration = options.duration ?? props.duration ?? defaultDuration

    const toast: ToastItem = {
      ...props,
      id,
      createdAt: Date.now(),
      progress: 0,
      duration,
    }

    toasts.value.push(toast)

    // Start progress animation and auto-dismiss
    if (duration > 0) {
      startProgress(id, duration)
    }

    return id
  }

  function startProgress(id: string, duration: number) {
    const startTime = Date.now()
    const interval = setInterval(() => {
      const toast = toasts.value.find(t => t.id === id)
      if (!toast) {
        clearInterval(interval)
        intervals.delete(id)
        return
      }

      const elapsed = Date.now() - startTime
      const progress = Math.min(elapsed / duration, 1)
      toast.progress = progress

      if (progress >= 1) {
        removeToast(id)
      }
    }, 16) // ~60fps

    intervals.set(id, interval)
  }

  function removeToast(id: string) {
    const index = toasts.value.findIndex(t => t.id === id)
    if (index > -1) {
      toasts.value.splice(index, 1)
    }

    const interval = intervals.get(id)
    if (interval) {
      clearInterval(interval)
      intervals.delete(id)
    }
  }

  function clearAll() {
    // Clear all intervals
    intervals.forEach(interval => clearInterval(interval))
    intervals.clear()

    // Clear all toasts
    toasts.value = []
  }

  function pauseToast(id: string) {
    const interval = intervals.get(id)
    if (interval) {
      clearInterval(interval)
      intervals.delete(id)
    }
  }

  function resumeToast(id: string) {
    const toast = toasts.value.find(t => t.id === id)
    if (!toast || !toast.duration) return

    const elapsed = (Date.now() - toast.createdAt) * toast.progress
    const remaining = toast.duration - elapsed

    if (remaining > 0) {
      startProgress(id, remaining)
    }
  }

  return {
    toasts: readonly(toasts),
    addToast,
    removeToast,
    clearAll,
    pauseToast,
    resumeToast,
  }
})
